import React from 'react';
import FocusTrap from 'focus-trap-react';
import useTranslation from "../../../../context/Translation/useTranslation";
import { useModal, ModalID } from '@repo/ui/src/modules/UI';
import CloseIcon from '@repo/ui/src/components/Icons/CloseIcon';
import { classNames } from '@repo/ui/src/utils/classNames';

export interface BaseModalProps {
  id: ModalID;
  children: React.ReactNode;
  className?: string;
  disableOutsideClose?: boolean;
  onClose?: () => void;
  fullScreen?: boolean;
  noButcherPaper?: boolean;
  disableClose?: boolean;
  'data-testid'?: string;
}

interface LabelledByProps extends BaseModalProps {
  'aria-labelledby': string;
  'aria-label'?: never;
}

interface LabelledProps extends BaseModalProps {
  'aria-labelledby'?: never;
  'aria-label'?: string;
}

export type ModalProps = LabelledByProps | LabelledProps;

const Modal = ({
  id,
  className,
  onClose,
  children,
  fullScreen,
  noButcherPaper = false,
  disableClose = false,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: ModalProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { active, zIndex, close } = useModal(id);
  const [hidden, setHidden] = React.useState(!active);
  const [visible, setVisible] = React.useState(active);

  React.useEffect(() => {
    if (active) {
      setHidden(false);
    } else {
      setVisible(false);
    }
  }, [active]);

  React.useEffect(() => {
    if (!hidden) {
      setVisible(true);
      ref.current?.focus();
    }
  }, [hidden]);

  const handleClose = () => {
    if (!disableClose) {
      onClose?.();
      close();
    }
  };
  if (hidden) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={classNames(
        'tw-fixed tw-inset-0 tw-grid tw-place-items-center motion-safe:tw-transition-all tw-duration-300',
        zIndex,
        visible ? 'tw-backdrop-blur-md' : 'tw-backdrop-blur-0 tw-delay-100',
      )}
      onTransitionEnd={(e) => {
        if (!visible) {
          setHidden(true);
        } else {
          e.currentTarget.querySelector('div')?.focus();
        }
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <FocusTrap active={active} focusTrapOptions={{ allowOutsideClick: !disableClose }}>
        <div
          role="dialog"
          className={classNames(
            'tw-relative tw-scroll-auto tw-overflow-auto motion-safe:tw-transition-opacity tw-grid tw-grid-areas-overlap',
            visible ? 'tw-delay-100 tw-opacity-100' : 'tw-opacity-0',
            !noButcherPaper && 'tw-bg-neutral-700',
            fullScreen
              ? 'tw-h-screen'
              : 'tw-max-h-[95vh] tw-border-brand-300 tw-border-2 tw-rounded-xl',
          )}
          data-testid={testId}
          aria-modal
          aria-labelledby={ariaLabelledBy}
          aria-label={ariaLabel}
          tabIndex={-1}
          aria-hidden={!active}
        >
          {!noButcherPaper && (
            <div className="tw-bg-butcher-paper tw-inset-0 tw-opacity-20 tw-select-none tw-grid-in-a" />
          )}
          {!disableClose && (
            <div className="tw-absolute tw-top-5 tw-right-5 tw-grid tw-justify-end tw-z-50">
              <button
                onClick={handleClose}
                className="tw-p-1"
                aria-label={t('genericContent.closeIconButtonLabel', undefined, {
                  default: 'Close',
                })}
              >
                <CloseIcon
                  className={classNames(!noButcherPaper ? 'tw-text-white' : 'tw-text-surface-600')}
                />
              </button>
            </div>
          )}
          <div className={classNames('tw-grid-in-a', fullScreen && 'tw-w-screen', className)}>
            {children}
          </div>
        </div>
      </FocusTrap>
    </div>
  );
};

export default Modal;
